import React, { useState } from 'react';
import SectionHeading from './SectionHeading';
import useScrollReveal from '../hooks/useScrollReveal';

/**
 * Written for consequence, not implementation. Each entry answers who depended on
 * the output and what changed because of it. The stack tags carry the technical
 * signal so the prose does not have to.
 */

const PROJECTS = [
  {
    id: 'forcing-engine',
    title: 'WRF-Hydro Forcing Engine',
    sponsor: 'NOAA National Water Model',
    summary:
      'NOAA’s National Water Model forecasts streamflow for over 3 million stream channels. The forcing pipeline is what feeds it. I was technical lead on that pipeline: restructuring it, and adding the capability it did not have.',
    metrics: [
      { v: '65', u: 'TB', l: 'Climate record built' },
      { v: '3M+', u: '', l: 'Stream channels downstream' },
      { v: '43', u: 'yrs', l: 'Record span, 1979 to 2022' },
    ],
    detail: [
      'The National Water Model is what the National Weather Service and emergency managers lean on for flood guidance. The pipeline was already running when I moved to the hydrometeorology program. I led the work on it from there: restructuring the code base, adding capability it did not have, and verifying it across the configurations it has to serve.',
      'Took it from hourly to sub-hourly input so fast events stopped being averaged away. Hourly data smooths over exactly the short intense bursts that drive flash flooding, and 2 minute radar lets the model see them.',
      'Pushed the short range forecast out to 48 hours, which is the difference between reacting to a flood and staging for one.',
      'Brought the Mountain Mapper terrain correction into the automated workflow. It had been run by hand, offline, in a legacy language. Folding it in removed a manual gate from a national forecast, and produced better precipitation estimates in complex terrain, which is exactly where the old ones were weakest.',
      'Restructured the code base and moved its configuration to a readable format, so standing up a new model or a new configuration stopped being a job only its authors could do.',
      'Built the 65 TB climate record covering 1979 to 2022 and published it as a USGS Data Release. Researchers now run studies off it instead of rebuilding the inputs themselves.',
    ],
    stack: ['Python', 'xarray', 'NetCDF', 'Zarr', 'ESMF regridding', 'pytest', 'HPC'],
  },
  {
    id: 'rfcps',
    title: 'Runway Friction and Closure Prediction',
    sponsor: 'Minneapolis Saint Paul and Denver international airports',
    summary:
      'Whether a runway is safe to land on during a winter storm, called hours ahead. Being wrong in either direction is expensive.',
    metrics: [
      { v: '2', u: '', l: 'Airports in operations' },
      { v: '5', u: 'yrs', l: 'Live through winters' },
    ],
    detail: [
      'Airport operations made real open and close decisions on this. Close a runway you did not need to and an airline loses millions. Leave one open that should have closed and an aircraft lands on ice.',
      'What went to operations was a closure guidance matrix, not a friction number. Staff needed something they could act on at 3 AM, so the output was the recommendation, with the diagnostics behind it if they wanted to argue with it.',
      'It corrected itself against live sensor readings as the night went on, rather than standing behind a forecast issued hours earlier.',
      'Freezing rain, snow and mixed events behave differently and no single model handles all of them, so it weighted three model families per storm instead of committing to one.',
      'Reported verification to the sponsor every two weeks, so it was judged on what actually happened rather than on how it was built.',
      'Extended to Denver: different climate, different runway layout, different data feeds.',
    ],
    stack: ['Python', 'scikit-learn', 'Ensemble methods', 'Bokeh', 'Real time delivery'],
  },
  {
    id: 'dicast-microservices',
    title: 'DICast Cloud Migration',
    sponsor: 'Skymet Weather Services',
    summary:
      'A commercial forecasting engine a client depended on, taken from one monolith to something they could scale and update a piece at a time.',
    metrics: [],
    detail: [
      'The client is an Indian weather services company. They needed to grow capacity and ship changes without redeploying the whole forecasting system, so I split it along its natural data boundaries. After that one part could change without putting the rest at risk.',
      'Several teams had to integrate against different parts of it, so each piece got a documented API rather than tribal knowledge about who to ask.',
      'Nobody in the group had built anything cloud native. I taught myself, built the prototype, and became the person colleagues came to for cloud architecture afterwards. My review recorded it in those words.',
    ],
    stack: ['Python', 'Flask', 'OpenAPI', 'Docker', 'AWS ECS', 'CloudWatch'],
  },
  {
    id: 'training-platform',
    title: 'Cloud Training Platform',
    sponsor: 'AGU, AMS, CUAHSI, USGS, University of Hawaii, Idaho DWR',
    summary:
      'Scientists anywhere learning NOAA’s flood model in a browser, with nothing to install. Technical and operational lead.',
    metrics: [
      { v: '150+', u: '', l: 'Trained at once' },
      { v: '6+', u: '', l: 'Organisations' },
    ],
    detail: [
      'WRF-Hydro is hard to install, and that was the real barrier to teaching it. During COVID it became the only barrier. Every participant got their own isolated environment in a browser instead.',
      'Ran for AGU, AMS, CUAHSI, the USGS, the University of Hawaii and Idaho’s water agency. 150 scientists at once at AMS.',
      'Trainers could push a dataset or a lesson to every participant mid-session rather than emailing files around.',
      'Environments shut down on logout, because the running cost was the thing most likely to kill the platform.',
      'I ran live operations during sessions. When environments failed to start at AGU I debugged it with the room waiting, which taught me more about reliability than any design review.',
    ],
    stack: ['AWS ECS', 'ECR', 'EFS', 'VPC', 'JupyterHub', 'Docker'],
  },
  {
    id: 'iwaa',
    title: 'Water Availability Assessment',
    sponsor: 'USGS',
    summary:
      'Which weather inputs actually decide whether a streamflow forecast is any good, answered against 42 years of record.',
    metrics: [
      { v: '1,500+', u: '', l: 'Basins calibrated' },
      { v: '42', u: 'yrs', l: 'Record analysed' },
    ],
    detail: [
      'The USGS had two candidate input datasets and had to pick one to build its national water availability assessment on. I designed the analysis that answered which, and why.',
      'The result told them which variables move forecast accuracy for which flow conditions, so effort went to the inputs that change the answer instead of being spread evenly across all of them.',
      'Calibrated over 1,500 basins on the USGS’s own supercomputer, getting NCAR tooling to run somewhere it was never written to run and inside their compute budget.',
    ],
    stack: ['Python', 'scikit-learn', 'SLURM', 'HPC', 'xarray'],
  },
  {
    id: 'nasa-drought',
    title: 'US Drought Monitor Calibration',
    sponsor: 'NASA',
    summary:
      'Got NCAR’s calibration tooling running on NASA’s infrastructure and NASA’s model, which had not been done before.',
    metrics: [],
    detail: [
      'The output feeds the US Drought Monitor, which farmers, water managers and policymakers use to make real decisions about water.',
      'NASA’s model variant and NASA’s supercomputer were both different from what the tool was written for. Making them meet was weeks of patient dependency and scheduler work, not one clever fix.',
      'Contributed to the open source release afterwards, so the next team does not have to repeat any of it.',
    ],
    stack: ['PyWrfHydroCalib', 'LIS WRF-Hydro', 'SLURM', 'MPI', 'HPC'],
  },
  {
    id: 'idaho',
    title: 'Idaho Cloud Seeding Assessment',
    sponsor: 'Idaho Department of Water Resources',
    summary:
      'Whether cloud seeding earns its keep across Idaho, and making the compute cheap enough that the question could be asked at all.',
    metrics: [
      { v: '<2', u: 'days', l: 'Per 20 year run' },
      { v: '20', u: 'yrs', l: 'Simulated per scenario' },
    ],
    detail: [
      'Every scenario meant a 20 year simulation. On a shared research queue that is a timeline nobody funds. On the right cloud machines it finished in under two days, and scenarios ran alongside each other instead of in line.',
      'The state had to decide what to spend before committing, so I benchmarked the options and handed them a cost and performance analysis to decide from. Client facing work, not an internal memo.',
      'It came in under budget without trimming the science.',
    ],
    stack: ['AWS EC2 HPC6a', 'Docker', 'WRF-Hydro', 'Cost modelling'],
  },
  {
    id: 'metplus',
    title: 'METplus Verification Framework',
    sponsor: 'NOAA, USAF, NRL, UK Met Office, Bureau of Meteorology',
    summary:
      'The framework national weather agencies use to judge whether their own forecasts are any good.',
    metrics: [{ v: '5+', u: '', l: 'National agencies' }],
    detail: [
      'Used by NOAA, the US Air Force, the Naval Research Laboratory, the UK Met Office and Australia’s Bureau of Meteorology. Verification is how those agencies know whether a model change actually improved anything.',
      'The same tool has to serve a researcher running a retrospective study and a forecaster verifying in real time. Reconciling those two audiences was most of the work.',
    ],
    stack: ['C++', 'Python', 'Verification statistics'],
  },
  {
    id: 'zarr',
    title: 'National Water Model in the Cloud',
    sponsor: 'NOAA',
    summary:
      'Decades of national flood model output made analysable where it sits, instead of by downloading it first.',
    metrics: [{ v: '4', u: '', l: 'Domains converted' }],
    detail: [
      'Anyone wanting to study National Water Model output used to have to pull terabytes down before starting. Converting it to a cloud native format meant researchers could query it in place.',
      'Covered the continental United States, Puerto Rico, Hawaii and south central Alaska, so the smaller domains got the same access as the mainland.',
    ],
    stack: ['Zarr', 'xarray', 'NetCDF', 'Cloud storage'],
  },
  {
    id: 'kuwait',
    title: 'Renewable Energy Forecasting',
    sponsor: 'Kuwait renewable energy program',
    summary:
      'What a solar or wind farm will produce before it produces it, which is the number the rest of the grid gets scheduled around.',
    metrics: [],
    detail: [
      'Grid operators commit to dispatch ahead of time. If the solar forecast is wrong, something else has to cover it, and that costs money or reliability.',
      'Built the training data that paired forecast irradiance and power predictions against what was actually measured on the ground.',
      'The verification work became part of a published evaluation of wind and solar power conversion methods in Energies.',
    ],
    stack: ['Python', 'MET', 'Verification'],
  },
  {
    id: 'dicast-core',
    title: 'DICast Forecasting Engine',
    sponsor: 'NCAR, Federal Highway Administration, energy and marine clients',
    summary:
      'The engine behind wildfire spot forecasts, winter road maintenance and grid operator wind and solar predictions. Where I started.',
    metrics: [],
    detail: [
      'Its output lands where being wrong costs something: firefighter safety on a fire line, whether a highway crew salts tonight, what a grid operator schedules around, and marine operations offshore.',
      'It learns from its own recent errors, so forecasts improve without anyone retuning it by hand.',
      'I joined as a student assistant on this one. It is the reason everything after it made sense.',
    ],
    stack: ['Python', 'C', 'Statistical blending', 'Machine learning'],
  },
];

const Project = ({ project, isOpen, onToggle, index }) => {
  const [ref, isVisible] = useScrollReveal({ threshold: 0.05 });
  const panelId = `project-panel-${project.id}`;

  return (
    <div
      ref={ref}
      className={`border-b border-ink/15 transition-all duration-700 ease-calm ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
    >
      <h3>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-controls={panelId}
          className="group flex w-full items-start gap-5 py-7 text-left md:gap-8"
        >
          <span className="metric mt-1.5 flex-none text-[0.7rem] tracking-eyebrow text-amber-deep">
            {String(index + 1).padStart(2, '0')}
          </span>

          <span className="flex-1">
            <span className="display-md block text-ink transition-colors duration-300 group-hover:text-amber-deep">
              {project.title}
            </span>
            <span className="mt-2 block font-mono text-[0.7rem] uppercase tracking-eyebrow text-ink-soft">
              {project.sponsor}
            </span>
          </span>

          <span
            aria-hidden="true"
            className="relative mt-3 h-3 w-3 flex-none text-ink-soft transition-colors duration-300 group-hover:text-amber-deep"
          >
            <span className="absolute left-0 top-1/2 h-px w-3 bg-current" />
            <span
              className={`absolute left-1/2 top-0 h-3 w-px bg-current transition-transform duration-300 ease-calm ${
                isOpen ? 'scale-y-0' : 'scale-y-100'
              }`}
            />
          </span>
        </button>
      </h3>

      <div
        id={panelId}
        hidden={!isOpen}
        className={isOpen ? 'pb-10 pl-0 md:pl-[3.25rem]' : ''}
      >
        <p className="text-[1.0625rem] leading-relaxed text-ink max-w-measure">
          {project.summary}
        </p>

        {project.metrics.length > 0 && (
          <dl className="mt-8 flex flex-wrap gap-x-12 gap-y-6">
            {project.metrics.map((m) => (
              <div key={m.l}>
                <dt className="sr-only">{m.l}</dt>
                <dd>
                  <span className="figure text-2xl text-ink">{m.v}</span>
                  {m.u && <span className="metric ml-1 text-xs text-amber-deep">{m.u}</span>}
                  <span className="mt-1.5 block font-mono text-[0.66rem] uppercase tracking-eyebrow text-ink-soft">
                    {m.l}
                  </span>
                </dd>
              </div>
            ))}
          </dl>
        )}

        <ul className="mt-8 space-y-4">
          {project.detail.map((d) => (
            <li key={d} className="flex gap-3.5">
              <span aria-hidden="true" className="mt-2.5 h-px w-3.5 flex-none bg-amber-deep/60" />
              <span className="prose-body">{d}</span>
            </li>
          ))}
        </ul>

        <div className="mt-8 flex flex-wrap gap-2">
          {project.stack.map((s) => (
            <span key={s} className="tag">
              {s}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const SelectedWork = () => {
  const [openId, setOpenId] = useState(PROJECTS[0].id);

  return (
    <section id="work" className="section bg-cream pt-0">
      <div className="shell">
        <SectionHeading
          number="03"
          label="Selected work"
          title="Eleven systems, most of them still running."
          intro="Every one of these had somebody acting on its output: a flood warning, a runway closure, a water allocation, a grid schedule."
        />

        <div className="mt-12">
          {PROJECTS.map((p, i) => (
            <Project
              key={p.id}
              project={p}
              index={i}
              isOpen={openId === p.id}
              onToggle={() => setOpenId(openId === p.id ? null : p.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default SelectedWork;
